import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Plus,
  Trash2,
  Edit,
  MapPin,
  Phone,
  CheckCircle,
  XCircle,
  DollarSign,
  AlertCircle,
  FileText,
  Package,
  Search
} from 'lucide-react';

interface Area {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface FacilityWasteType {
  waste_type: string;
  last_known_price?: string;
}

interface Facility {
  id: string;
  area_id: string;
  name: string;
  address: string;
  contact_number?: string;
  has_account: boolean;
  conditions?: string;
  asbestos_conditions?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  waste_types?: FacilityWasteType[];
}

const WASTE_TYPES = [
  { value: 'asbestos', label: 'Asbestos' },
  { value: 'demolition_waste', label: 'Demolition Waste' },
  { value: 'bricks_tile', label: 'Bricks & Tile' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'soil', label: 'Soil' },
  { value: 'green_waste', label: 'Green Waste' },
  { value: 'steel', label: 'Steel' },
  { value: 'timber', label: 'Timber' }
];

export const WasteDisposalFacilitiesPage: React.FC = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedWasteTypes, setExpandedWasteTypes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchAreas(), fetchFacilities()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const { data, error } = await supabase
        .from('waste_disposal_areas')
        .select('*')
        .order('name');

      if (error) throw error;
      setAreas(data || []);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const { data: facilitiesData, error: facilitiesError } = await supabase
        .from('waste_disposal_facilities')
        .select('*')
        .order('name');

      if (facilitiesError) throw facilitiesError;

      const facilitiesWithTypes = await Promise.all(
        (facilitiesData || []).map(async (facility) => {
          const { data: wasteTypesData } = await supabase
            .from('facility_waste_types')
            .select('waste_type, last_known_price')
            .eq('facility_id', facility.id);

          return {
            ...facility,
            waste_types: wasteTypesData || []
          };
        })
      );

      setFacilities(facilitiesWithTypes);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const toggleArea = (areaId: string) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(areaId)) {
      newExpanded.delete(areaId);
    } else {
      newExpanded.add(areaId);
    }
    setExpandedAreas(newExpanded);
  };

  const toggleWasteType = (areaId: string, wasteType: string) => {
    const key = `${areaId}-${wasteType}`;
    const newExpanded = new Set(expandedWasteTypes);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedWasteTypes(newExpanded);
  };

  const handleDeleteArea = async (areaId: string) => {
    if (confirm('Are you sure you want to delete this area? This will also delete all facilities in this area.')) {
      try {
        const { error } = await supabase
          .from('waste_disposal_areas')
          .delete()
          .eq('id', areaId);

        if (error) throw error;
        fetchData();
      } catch (error) {
        console.error('Error deleting area:', error);
        alert('Failed to delete area. Please try again.');
      }
    }
  };

  const handleDeleteFacility = async (facilityId: string) => {
    if (confirm('Are you sure you want to delete this facility?')) {
      try {
        const { error } = await supabase
          .from('waste_disposal_facilities')
          .delete()
          .eq('id', facilityId);

        if (error) throw error;
        fetchData();
      } catch (error) {
        console.error('Error deleting facility:', error);
        alert('Failed to delete facility. Please try again.');
      }
    }
  };

  const getFacilitiesByAreaAndWasteType = (areaId: string) => {
    const areaFacilities = facilities.filter(f => f.area_id === areaId);
    const grouped: { [key: string]: Facility[] } = {};

    WASTE_TYPES.forEach(wasteType => {
      grouped[wasteType.value] = areaFacilities.filter(f =>
        f.waste_types?.some(wt => wt.waste_type === wasteType.value)
      );
    });

    return grouped;
  };

  const filteredAreas = areas.filter(area =>
    area.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waste Disposal Facilities</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage disposal areas and facilities by waste type
          </p>
        </div>
        <button
          onClick={() => {
            setEditingArea(null);
            setShowAreaModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Area</span>
        </button>
      </div>

      {areas.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search areas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      )}

      {areas.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No areas found</h3>
          <p className="text-gray-500 mb-4">Create your first area to start adding waste disposal facilities.</p>
          <button
            onClick={() => {
              setEditingArea(null);
              setShowAreaModal(true);
            }}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Add First Area
          </button>
        </div>
      ) : filteredAreas.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No areas match your search</h3>
          <p className="text-gray-500 mb-4">Try adjusting your search term.</p>
          <button
            onClick={() => setSearchTerm('')}
            className="text-orange-600 hover:text-orange-700 font-medium"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredAreas.map(area => {
            const isExpanded = expandedAreas.has(area.id);
            const groupedFacilities = getFacilitiesByAreaAndWasteType(area.id);
            const facilityCount = facilities.filter(f => f.area_id === area.id).length;

            return (
              <div key={area.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div
                  className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) {
                      return;
                    }
                    toggleArea(area.id);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-6 w-6 text-orange-600" />
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">{area.name}</h2>
                        <p className="text-sm text-gray-500">{facilityCount} facilities</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAreaId(area.id);
                          setEditingFacility(null);
                          setShowFacilityModal(true);
                        }}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-md flex items-center space-x-1 hover:bg-green-700 transition-colors text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Facility</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingArea(area);
                          setShowAreaModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-2"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteArea(area.id);
                        }}
                        className="text-red-600 hover:text-red-900 p-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <div className="text-gray-600 p-2">
                        {isExpanded ? '▼' : '▶'}
                      </div>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-6 space-y-4">
                    {WASTE_TYPES.map(wasteType => {
                      const typeFacilities = groupedFacilities[wasteType.value];
                      if (typeFacilities.length === 0) return null;

                      const wasteTypeKey = `${area.id}-${wasteType.value}`;
                      const isWasteTypeExpanded = expandedWasteTypes.has(wasteTypeKey);

                      return (
                        <div key={wasteType.value} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div
                            className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleWasteType(area.id, wasteType.value)}
                          >
                            <div className="flex items-center space-x-2">
                              <Package className="h-5 w-5 text-gray-600" />
                              <h3 className="text-base font-medium text-gray-900">{wasteType.label}</h3>
                              <span className="text-sm text-gray-500">({typeFacilities.length})</span>
                            </div>
                            <div className="text-gray-600">
                              {isWasteTypeExpanded ? '▼' : '▶'}
                            </div>
                          </div>

                          {isWasteTypeExpanded && (
                            <div className="p-4 space-y-2">
                              {typeFacilities.map(facility => {
                                const wasteTypeInfo = facility.waste_types?.find(wt => wt.waste_type === wasteType.value);
                                return (
                                  <div
                                    key={facility.id}
                                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-sm transition-shadow"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <h4 className="text-base font-semibold text-gray-900 mb-1">{facility.name}</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                          <div className="flex items-start space-x-2">
                                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-700">{facility.address}</span>
                                          </div>

                                          {facility.contact_number && (
                                            <div className="flex items-center space-x-2">
                                              <Phone className="h-4 w-4 text-gray-400" />
                                              <span className="text-gray-700">{facility.contact_number}</span>
                                            </div>
                                          )}

                                          <div className="flex items-center space-x-2">
                                            {facility.has_account ? (
                                              <>
                                                <CheckCircle className="h-4 w-4 text-green-600" />
                                                <span className="text-green-700 font-medium">Have Account</span>
                                              </>
                                            ) : (
                                              <>
                                                <XCircle className="h-4 w-4 text-gray-400" />
                                                <span className="text-gray-500">No Account</span>
                                              </>
                                            )}
                                          </div>

                                          {wasteTypeInfo?.last_known_price && (
                                            <div className="flex items-center space-x-2">
                                              <DollarSign className="h-4 w-4 text-gray-400" />
                                              <span className="text-gray-700 font-medium">{wasteTypeInfo.last_known_price}</span>
                                            </div>
                                          )}
                                        </div>

                                        {wasteType.value === 'asbestos' && facility.asbestos_conditions && (
                                          <div className="flex items-start space-x-2 bg-red-50 p-2 rounded border border-red-200 mt-2">
                                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1">
                                              <span className="text-red-900 text-xs font-semibold block mb-0.5">Asbestos Conditions:</span>
                                              <span className="text-red-800 text-xs">{facility.asbestos_conditions}</span>
                                            </div>
                                          </div>
                                        )}

                                        {facility.conditions && (
                                          <div className="flex items-start space-x-2 bg-yellow-50 p-2 rounded border border-yellow-200 mt-2">
                                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-yellow-800 text-xs">{facility.conditions}</span>
                                          </div>
                                        )}

                                        {facility.notes && (
                                          <div className="flex items-start space-x-2 mt-2">
                                            <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-600 text-xs">{facility.notes}</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex space-x-1 ml-4">
                                        <button
                                          onClick={() => {
                                            setEditingFacility(facility);
                                            setSelectedAreaId(facility.area_id);
                                            setShowFacilityModal(true);
                                          }}
                                          className="text-blue-600 hover:text-blue-900 p-1"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteFacility(facility.id)}
                                          className="text-red-600 hover:text-red-900 p-1"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {Object.values(groupedFacilities).every(arr => arr.length === 0) && (
                      <div className="text-center py-8">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No facilities in this area</h3>
                        <p className="text-gray-500 mb-4">Add your first facility to get started.</p>
                        <button
                          onClick={() => {
                            setSelectedAreaId(area.id);
                            setEditingFacility(null);
                            setShowFacilityModal(true);
                          }}
                          className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                        >
                          Add First Facility
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAreaModal && (
        <AreaModal
          area={editingArea}
          onClose={() => {
            setShowAreaModal(false);
            setEditingArea(null);
          }}
          onSave={() => {
            setShowAreaModal(false);
            setEditingArea(null);
            fetchData();
          }}
        />
      )}

      {showFacilityModal && selectedAreaId && (
        <FacilityModal
          facility={editingFacility}
          areaId={selectedAreaId}
          onClose={() => {
            setShowFacilityModal(false);
            setEditingFacility(null);
            setSelectedAreaId(null);
          }}
          onSave={() => {
            setShowFacilityModal(false);
            setEditingFacility(null);
            setSelectedAreaId(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

const AreaModal: React.FC<{
  area: Area | null;
  onClose: () => void;
  onSave: () => void;
}> = ({ area, onClose, onSave }) => {
  const [name, setName] = useState(area?.name || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (area) {
        const { error } = await supabase
          .from('waste_disposal_areas')
          .update({ name })
          .eq('id', area.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('waste_disposal_areas')
          .insert([{ name }]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving area:', error);
      alert('Failed to save area. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {area ? 'Edit Area' : 'Add Area'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Area Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="e.g., Sydney, Albury"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FacilityModal: React.FC<{
  facility: Facility | null;
  areaId: string;
  onClose: () => void;
  onSave: () => void;
}> = ({ facility, areaId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: facility?.name || '',
    address: facility?.address || '',
    contact_number: facility?.contact_number || '',
    has_account: facility?.has_account || false,
    conditions: facility?.conditions || '',
    asbestos_conditions: facility?.asbestos_conditions || '',
    notes: facility?.notes || ''
  });
  const [wasteTypePrices, setWasteTypePrices] = useState<{[key: string]: string}>(
    facility?.waste_types?.reduce((acc, wt) => {
      acc[wt.waste_type] = wt.last_known_price || '';
      return acc;
    }, {} as {[key: string]: string}) || {}
  );
  const [selectedWasteTypes, setSelectedWasteTypes] = useState<string[]>(
    facility?.waste_types?.map(wt => wt.waste_type) || []
  );
  const [submitting, setSubmitting] = useState(false);

  console.log('FacilityModal - selectedWasteTypes:', selectedWasteTypes);
  console.log('FacilityModal - includes asbestos?', selectedWasteTypes.includes('asbestos'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedWasteTypes.length === 0) {
      alert('Please select at least one waste type');
      return;
    }

    setSubmitting(true);

    try {
      let facilityId = facility?.id;

      if (facility) {
        const { error } = await supabase
          .from('waste_disposal_facilities')
          .update(formData)
          .eq('id', facility.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('waste_disposal_facilities')
          .insert([{ ...formData, area_id: areaId }])
          .select()
          .single();

        if (error) throw error;
        facilityId = data.id;
      }

      const { error: deleteError } = await supabase
        .from('facility_waste_types')
        .delete()
        .eq('facility_id', facilityId);

      if (deleteError) throw deleteError;

      const wasteTypeInserts = selectedWasteTypes.map(waste_type => ({
        facility_id: facilityId,
        waste_type,
        last_known_price: wasteTypePrices[waste_type] || null
      }));

      const { error: insertError } = await supabase
        .from('facility_waste_types')
        .insert(wasteTypeInserts);

      if (insertError) throw insertError;

      onSave();
    } catch (error) {
      console.error('Error saving facility:', error);
      alert('Failed to save facility. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWasteType = (wasteType: string) => {
    if (selectedWasteTypes.includes(wasteType)) {
      setSelectedWasteTypes(selectedWasteTypes.filter(wt => wt !== wasteType));
      const newPrices = { ...wasteTypePrices };
      delete newPrices[wasteType];
      setWasteTypePrices(newPrices);
    } else {
      setSelectedWasteTypes([...selectedWasteTypes, wasteType]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {facility ? 'Edit Facility' : 'Add Facility'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facility Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Number
            </label>
            <input
              type="text"
              value={formData.contact_number}
              onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.has_account}
                onChange={(e) => setFormData({ ...formData, has_account: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">We have an account with this facility</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Waste Types Accepted <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {WASTE_TYPES.map(wasteType => (
                <div key={wasteType.value} className="border border-gray-200 rounded-md p-3">
                  <label className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedWasteTypes.includes(wasteType.value)}
                      onChange={() => toggleWasteType(wasteType.value)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{wasteType.label}</span>
                  </label>
                  {selectedWasteTypes.includes(wasteType.value) && (
                    <div className="ml-6">
                      <label className="block text-xs text-gray-600 mb-1">
                        Price for {wasteType.label}
                      </label>
                      <input
                        type="text"
                        value={wasteTypePrices[wasteType.value] || ''}
                        onChange={(e) => setWasteTypePrices({
                          ...wasteTypePrices,
                          [wasteType.value]: e.target.value
                        })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="e.g., $150 per tonne"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {selectedWasteTypes.includes('asbestos') && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <label className="block text-sm font-bold text-red-900 mb-2">
                Asbestos-Specific Conditions
              </label>
              <textarea
                value={formData.asbestos_conditions}
                onChange={(e) => setFormData({ ...formData, asbestos_conditions: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={3}
                placeholder="e.g., Hand unload only, no tipper trucks allowed. Max truck size: 8 tonnes"
              />
              <p className="text-xs text-red-600 mt-1">
                Specify any special handling requirements for asbestos waste at this facility
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              General Conditions
            </label>
            <textarea
              value={formData.conditions}
              onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
              placeholder="Any other general conditions or requirements"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
              placeholder="General notes about the facility"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
